import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import formStyles from 'pages/Form.module.css';
import styles from 'pages/NewMemberSettings.module.css';
import globalStyles from 'Global.module.css';
import { SetMembershipQuestionsDocument, DeleteMembershipQuestionDocument } from 'graphql/mutations.generated';
import { GetQuestionsDocument } from 'graphql/queries.generated';

interface Question {
  organizationMembershipQuestionId: string;
  organizationId: string;
  question: string;
}

const NewMemberSettings: React.FC = () => {
    const { organizationId } = useParams<{ organizationId: string }>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { data, loading: queryLoading, error: queryError, refetch } = useQuery(GetQuestionsDocument, {
    variables: { organizationId: organizationId || '' },
    skip: !organizationId,
  });

  const [setMembershipQuestions, { loading: mutationLoading }] = useMutation(SetMembershipQuestionsDocument);
  const [deleteMembershipQuestion] = useMutation(DeleteMembershipQuestionDocument);

  useEffect(() => {
    if (data?.questions) {
      setQuestions(data.questions.map(q => ({
        organizationMembershipQuestionId: q.organizationMembershipQuestionId,
        organizationId: q.organizationId,
        question: q.question,
      })));
    }
  }, [data]);

  const handleQuestionChange = (index: number, value: string) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index].question = value;
    setQuestions(updatedQuestions);
  };

  const handleAddQuestion = () => {
    if (!organizationId) return;
    
    setQuestions([
      ...questions,
      {
        organizationMembershipQuestionId: '',
        organizationId: organizationId,
        question: '',
      },
    ]);
  };

  const handleRemoveQuestion = async (index: number) => {
    
    await deleteMembershipQuestion({
        variables: {
            organizationId: organizationId || '',
            organizationMembershipQuestionId: questions[index].organizationMembershipQuestionId,
        },
    });
    await refetch();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!organizationId) {
      setError('No organization ID provided');
      return;
    }

    const filteredQuestions = questions.filter(q => q.question.trim() !== '');

    try {
      await setMembershipQuestions({
        variables: {
          organizationId,
          questions: filteredQuestions.map(q => ({
            organizationMembershipQuestionId: q.organizationMembershipQuestionId,
            organizationId: q.organizationId,
            question: q.question,
          })),
        },
      });

      setSuccessMessage('Membership questions saved successfully!');
      
      await refetch();
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while saving');
    }
  };

  if (!organizationId) {
    return (
      <div className={formStyles.formContainer}>
        <h1>New Member Settings</h1>
        <p className={formStyles.error}>No organization ID provided</p>
      </div>
    );
  }

  if (queryLoading) {
    return (
      <div className={formStyles.formContainer}>
        <h1>New Member Settings</h1>
        <p>Loading questions...</p>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className={formStyles.formContainer}>
        <h1>New Member Settings</h1>
        <p className={formStyles.error}>Error loading questions: {queryError.message}</p>
      </div>
    );
  }

  return (
    <div className={`${formStyles.formContainer} ${styles.newMemberSettings}`}>
      <h1>New Member Settings</h1>
      <p className={styles.description}>
        Configure questions that new members will be asked when joining this group.
      </p>
      <form onSubmit={handleSubmit}>
        <div className={styles.questionsList}>
          {questions.length === 0 ? (
            <p className={styles.noQuestions}>No questions yet. Click "Add Question" to create one.</p>
          ) : (
            questions.map((question, index) => (
              <div key={index} className={styles.questionItem}>
                <div className={styles.questionInput}>
                  <label htmlFor={`question-${index}`}>Question {index + 1}:</label>
                  <input
                    type="text"
                    id={`question-${index}`}
                    value={question.question}
                    onChange={(e) => handleQuestionChange(index, e.target.value)}
                    placeholder="Enter your membership question"
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveQuestion(index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={handleAddQuestion}
          className={styles.addButton}
        >
          Add Question
        </button>
        {successMessage && <div className={formStyles.success}>{successMessage}</div>}
        {error && <div className={formStyles.error}>{error}</div>}
              <button type="submit" disabled={mutationLoading || questions.length === 0} className={globalStyles.submitButton}>
          {mutationLoading ? 'Saving...' : 'Save Questions'}
        </button>
      </form>
    </div>
  );
};

export default NewMemberSettings;

